<template>
  <div class="max-w-2xl mx-auto py-8 px-4">
    <h2 class="text-2xl font-bold mb-6 text-theme-text">Settings</h2>

    <!-- Theme Settings -->
    <div class="bg-theme-card p-6 rounded-xl shadow-md border border-theme-border mb-6">
      <h3 class="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
        <i class="pi pi-palette text-primary-600"></i>
        Appearance
      </h3>

      <div class="space-y-5">
        <!-- Light / Dark toggle -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-3">Mode</label>
          <div class="grid grid-cols-2 gap-3">
            <!-- Light Theme -->
            <button
              @click="setThemeLocal('light', $event)"
              :class="[
                'relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200',
                !isDark
                  ? 'border-primary-600 bg-primary-600/10 shadow-md'
                  : 'border-theme-border hover:border-primary-400'
              ]"
            >
              <div class="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shadow-sm">
                <i class="pi pi-sun text-primary-500 text-lg"></i>
              </div>
              <div class="text-left">
                <p class="font-medium text-theme-text text-sm">Light</p>
                <p class="text-xs text-theme-text2">Classic bright theme</p>
              </div>
              <i
                v-if="!isDark"
                class="pi pi-check-circle text-primary-600 absolute top-2 right-2"
              ></i>
            </button>

            <!-- Dark Theme (picks the active dark variant) -->
            <button
              @click="setThemeLocal(activeDarkTheme, $event)"
              :class="[
                'relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200',
                isDark
                  ? 'border-primary-600 bg-primary-600/10 shadow-md'
                  : 'border-theme-border hover:border-primary-400'
              ]"
            >
              <div class="w-10 h-10 rounded-full bg-neutral-800 border border-neutral-600 flex items-center justify-center shadow-sm">
                <i class="pi pi-moon text-indigo-300 text-lg"></i>
              </div>
              <div class="text-left">
                <p class="font-medium text-theme-text text-sm">Dark</p>
                <p class="text-xs text-theme-text2">Easy on the eyes</p>
              </div>
              <i
                v-if="isDark"
                class="pi pi-check-circle text-primary-600 absolute top-2 right-2"
              ></i>
            </button>
          </div>
        </div>

        <!-- Dark Theme Variants (visible when dark mode is active) -->
        <transition name="fade-slide">
          <div v-if="isDark">
            <label class="block text-sm font-medium text-theme-text2 mb-3">Dark Theme Variant</label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <button
                v-for="dk in darkThemeOptions"
                :key="dk.key"
                @click="setThemeLocal(dk.key, $event)"
                :class="[
                  'relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-200',
                  currentTheme === dk.key
                    ? 'border-primary-600 shadow-md'
                    : 'border-theme-border hover:border-primary-400/50'
                ]"
                :style="{ background: dk.preview.bg }"
              >
                <!-- Color preview dots -->
                <div class="flex gap-1.5 shrink-0">
                  <span class="w-4 h-4 rounded-full border border-white/10" :style="{ background: dk.preview.card }"></span>
                  <span class="w-4 h-4 rounded-full border border-white/10" :style="{ background: dk.preview.border }"></span>
                  <span class="w-4 h-4 rounded-full border border-white/10" :style="{ background: dk.preview.input }"></span>
                </div>
                <div class="text-left min-w-0">
                  <p class="font-medium text-sm truncate" :style="{ color: dk.preview.text }">{{ dk.label }}</p>
                  <p class="text-xs truncate" :style="{ color: dk.preview.muted }">{{ dk.desc }}</p>
                </div>
                <i
                  v-if="currentTheme === dk.key"
                  class="pi pi-check-circle text-primary-600 absolute top-2 right-2 text-sm"
                ></i>
              </button>
            </div>
          </div>
        </transition>

        <!-- Preview -->
        <div class="mt-4 p-4 rounded-lg bg-theme-bg2 border border-theme-border">
          <p class="text-xs text-theme-text2 mb-2">Preview</p>
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded bg-primary-600"></div>
            <div class="w-8 h-8 rounded bg-primary-400"></div>
            <div class="w-8 h-8 rounded bg-secondary-500"></div>
            <div class="w-8 h-8 rounded bg-theme-card border border-theme-border"></div>
            <div class="flex-1 h-2 rounded bg-theme-border"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Preferences Placeholder -->
    <div class="bg-theme-card p-6 rounded-xl shadow-md border border-theme-border">
      <h3 class="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
        <i class="pi pi-cog text-primary-600"></i>
        Preferences
      </h3>
      <p class="text-sm text-theme-text2">More settings coming soon.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useTheme } from '@/composables/useTheme';
import { themes as themesDef, darkThemeKeys } from '@/config/theme';

const { currentTheme, isDark, setTheme, darkThemeKeys: dtKeys } = useTheme();

// Remember the last-used dark variant so clicking "Dark" mode button re-applies it
const DARK_PREF_KEY = 'usenlease-dark-variant';
const activeDarkTheme = computed(() => {
  if (isDark.value) return currentTheme.value;
  return localStorage.getItem(DARK_PREF_KEY) || 'dark';
});

// When switching dark variants, persist preference
const originalSetTheme = setTheme;
const wrappedSetTheme = (name, event) => {
  originalSetTheme(name, event);
  if (darkThemeKeys.includes(name)) {
    localStorage.setItem(DARK_PREF_KEY, name);
  }
};
// Override for template
const setThemeWrapped = wrappedSetTheme;

// Dark theme options with preview colors & descriptions
const darkThemeOptions = darkThemeKeys.map(key => {
  const t = themesDef[key];
  const descs = {
    'dark':          'VS Code default dark palette',
    'dark-dimmed':   'Muted blue-gray, very gentle',
    'dark-oceanic':  'Deep sea teal, relaxing',
    'dark-monokai':  'Warm charcoal, classic dev feel',
    'dark-nord':     'Cool arctic blue, soft & soothing',
  };
  return {
    key,
    label: t.name,
    desc: descs[key] || '',
    preview: {
      bg:     t.bg,
      card:   t.cardBg,
      border: t.border,
      input:  t.inputBg,
      text:   t.text,
      muted:  t.textMuted,
    },
  };
});

const settings = ref({});

onMounted(() => {
  // Fetch settings from your API
});

// Alias for template to use the wrapped version
const setThemeLocal = (name, event) => {
  wrappedSetTheme(name, event);
};
</script>

<script>
// Expose in a way template can use cleanly
</script>

<style scoped>
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>